import { api } from '@/lib/api';
import { SubmissionFormValues } from '@/components/submissions/SubmissionForm';

export interface DraftSubmissionResponse {
  message: string;
  draftId: string;
  lastModified: string;
}

export interface DraftSubmissionInfo {
  _id: string;
  lastModified: string;
  hasFiles: boolean;
  createdAt: string;
}

export interface GetDraftSubmissionResponse {
  draft: Partial<SubmissionFormValues>; // Draft submission data with form structure
}

class DraftSubmissionService {
  async saveDraft(projectId: string, formData: FormData): Promise<DraftSubmissionResponse> {
    const response = await api.post(`/projects/${projectId}/draft-submission`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getDraft(projectId: string): Promise<GetDraftSubmissionResponse> {
    const response = await api.get(`/projects/${projectId}/draft-submission`);
    return response.data;
  }

  async deleteDraft(projectId: string): Promise<{ message: string }> {
    const response = await api.delete(`/projects/${projectId}/draft-submission`);
    return response.data;
  }

  // Helper method to create FormData from form values
  createFormDataFromValues(
    values: Partial<SubmissionFormValues>, 
    files: Record<string, File | File[]>
  ): FormData {
    const formData = new FormData();

    // Add all form fields
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'files') return; // Skip files object, handle separately
      
      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
      } else if (typeof value === 'object' && value !== null) {
        formData.append(key, JSON.stringify(value));
      } else if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    // Add files
    Object.entries(files).forEach(([fieldName, fileOrFiles]) => {
      if (fileOrFiles) {
        if (Array.isArray(fileOrFiles)) {
          fileOrFiles.forEach(file => {
            formData.append(fieldName, file);
          });
        } else {
          formData.append(fieldName, fileOrFiles);
        }
      }
    });

    return formData;
  }
}

export const draftSubmissionService = new DraftSubmissionService();
